import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ClipboardCheck, Plus, ArrowRight, Search, Trash2, Eye, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";
import { DropdownBadge, DropdownSelect } from "../components/shared/DropdownHooks";

export default function AuditMgmtPage({ onLogout, userRole, isFeatureEnabled }) {
  const [audits, setAudits] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailAudit, setDetailAudit] = useState(null);
  const [findings, setFindings] = useState([]);
  const [findingForm, setFindingForm] = useState({ title: "", description: "", severity: "observation", area: "" });
  const [form, setForm] = useState({ title: "", audit_type: "internal", description: "", scope: "", lead_auditor: "", scheduled_date: "", checklist: [{ text: "" }] });
  const isAdmin = userRole === "admin";
  const NEXT = { planned: "in_progress", in_progress: "findings_open", findings_open: "closed" };

  const fetchAll = useCallback(async () => {
    try {
      const [aR, sR] = await Promise.allSettled([
        axios.get(`${API}/audits`, { headers: getAuthHeaders() }),
        axios.get(`${API}/audits/stats`, { headers: getAuthHeaders() }),
      ]);
      if (aR.status === "fulfilled") setAudits(aR.value.data || []);
      if (sR.status === "fulfilled") setStats(sR.value.data || {});
    } catch {}
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const addChecklistItem = () => setForm(f => ({ ...f, checklist: [...f.checklist, { text: "" }] }));
  const updateChecklistItem = (i, val) => setForm(f => ({ ...f, checklist: f.checklist.map((c, idx) => idx === i ? { ...c, text: val } : c) }));
  const removeChecklistItem = (i) => setForm(f => ({ ...f, checklist: f.checklist.filter((_, idx) => idx !== i) }));

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    try {
      const payload = { ...form, checklist: form.checklist.filter(c => c.text.trim()) };
      await axios.post(`${API}/audits`, payload, { headers: getAuthHeaders() });
      toast.success("Audit created"); setCreateOpen(false);
      setForm({ title: "", audit_type: "internal", description: "", scope: "", lead_auditor: "", scheduled_date: "", checklist: [{ text: "" }] });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const advance = async (aid, newStatus) => {
    try {
      await axios.post(`${API}/audits/${aid}/transition`, { status: newStatus }, { headers: getAuthHeaders() });
      toast.success(`Audit → ${newStatus.replace(/_/g, " ")}`); fetchAll();
      if (detailAudit?.id === aid) refreshDetail(aid);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const refreshDetail = async (aid) => {
    try {
      const [aR, fR] = await Promise.all([
        axios.get(`${API}/audits/${aid}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/audits/${aid}/findings`, { headers: getAuthHeaders() }),
      ]);
      setDetailAudit(aR.data);
      setFindings(fR.data || []);
    } catch {}
  };

  const addFinding = async () => {
    if (!findingForm.title.trim() || !detailAudit) return;
    try {
      await axios.post(`${API}/audits/${detailAudit.id}/findings`, findingForm, { headers: getAuthHeaders() });
      toast.success("Finding added"); setFindingForm({ title: "", description: "", severity: "observation", area: "" });
      refreshDetail(detailAudit.id); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const closeFinding = async (fid) => {
    try { await axios.put(`${API}/audit-findings/${fid}`, { status: "closed" }, { headers: getAuthHeaders() }); toast.success("Finding closed"); refreshDetail(detailAudit.id); fetchAll(); }
    catch { toast.error("Failed"); }
  };

  const deleteAudit = async (id) => {
    if (!window.confirm("Delete this audit and all findings?")) return;
    try { await axios.delete(`${API}/audits/${id}`, { headers: getAuthHeaders() }); toast.success("Deleted"); fetchAll(); if (detailAudit?.id === id) setDetailAudit(null); }
    catch { toast.error("Failed"); }
  };

  if (loading) return <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}><div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#2563EB]" size={28} /></div></Layout>;

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="audit-mgmt-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <PageHeader title="Audit Management" subtitle="Schedule audits, manage checklists, track findings" testId="audit-mgmt-title" />
          <Button onClick={() => setCreateOpen(true)} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-none text-xs font-bold" data-testid="create-audit-btn"><Plus size={14} className="mr-1" /> New Audit</Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="audit-stats">
          {[["Total", stats.total, "#0F172A"], ["Planned", stats.planned, "#94A3B8"], ["In Progress", stats.in_progress, "#2563EB"], ["Findings Open", stats.findings_open, "#D97706"], ["Closed", stats.closed, "#10B981"], ["Total Findings", stats.total_findings, "#7C3AED"], ["Open Findings", stats.open_findings, "#DC2626"]].map(([l, v, c]) => (
            <div key={l} className="bg-white border border-[#E2E8F0] px-4 py-3"><p className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">{l}</p><p className="text-2xl font-heading font-black mt-0.5" style={{ color: c }}>{v || 0}</p></div>
          ))}
        </div>

        <div className="bg-white border border-[#E2E8F0]" data-testid="audit-list">
          {audits.length === 0 ? <div className="p-12 text-center text-sm text-[#94A3B8]"><ClipboardCheck size={36} className="mx-auto mb-2 text-[#CBD5E1]" />No audits scheduled.</div> : (
            <div className="divide-y divide-[#F1F5F9]">
              {audits.map(a => (
                <div key={a.id} className="px-4 py-3 flex items-center justify-between hover:bg-[#FAFAFA]" data-testid={`audit-${a.id}`}>
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => refreshDetail(a.id)}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs text-[#2563EB] font-bold">{a.audit_number}</span>
                      <DropdownBadge dropdownKey="audit_statuses" value={a.status} />
                      <DropdownBadge dropdownKey="audit_types" value={a.audit_type} />
                    </div>
                    <p className="text-sm font-medium text-[#0F172A] mt-1">{a.title}</p>
                    <p className="text-[11px] text-[#64748B]">Lead: {a.lead_auditor || "—"} · {a.checklist_count} checklist items · {a.scheduled_date || "Not scheduled"}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {NEXT[a.status] && <Button size="sm" onClick={() => advance(a.id, NEXT[a.status])} className="rounded-none bg-[#2563EB] text-white text-[10px] font-bold" data-testid={`advance-audit-${a.id}`}><ArrowRight size={11} className="mr-1" />{NEXT[a.status].replace(/_/g, " ")}</Button>}
                    <Button size="sm" variant="outline" onClick={() => refreshDetail(a.id)} className="rounded-none text-[10px]"><Eye size={11} /></Button>
                    {isAdmin && <Button size="sm" variant="outline" onClick={() => deleteAudit(a.id)} className="rounded-none border-[#FECACA] text-[#DC2626] text-[10px]"><Trash2 size={11} /></Button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Audit */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="create-audit-dialog">
          <DialogHeader><DialogTitle>New Audit</DialogTitle><DialogDescription className="text-xs text-[#64748B]">Schedule an audit with checklist items.</DialogDescription></DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[10px] font-bold uppercase text-[#64748B]">Title *</Label><Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="mt-1 rounded-none text-sm" data-testid="audit-title" /></div>
              <div><Label className="text-[10px] font-bold uppercase text-[#64748B]">Type</Label><DropdownSelect dropdownKey="audit_types" value={form.audit_type} onChange={e => setForm(f => ({ ...f, audit_type: e.target.value }))} className="mt-1 w-full border border-[#E2E8F0] p-2 text-sm" testId="audit-type" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[10px] font-bold uppercase text-[#64748B]">Lead Auditor</Label><Input value={form.lead_auditor} onChange={e => setForm(f => ({ ...f, lead_auditor: e.target.value }))} className="mt-1 rounded-none text-sm" data-testid="audit-lead" /></div>
              <div><Label className="text-[10px] font-bold uppercase text-[#64748B]">Scheduled Date</Label><Input type="date" value={form.scheduled_date} onChange={e => setForm(f => ({ ...f, scheduled_date: e.target.value }))} className="mt-1 rounded-none text-sm" data-testid="audit-date" /></div>
            </div>
            <div><Label className="text-[10px] font-bold uppercase text-[#64748B]">Scope</Label><textarea value={form.scope} onChange={e => setForm(f => ({ ...f, scope: e.target.value }))} className="mt-1 w-full border border-[#E2E8F0] p-2 text-sm h-14 resize-none" data-testid="audit-scope" /></div>
            <div className="border-t border-[#E2E8F0] pt-3">
              <div className="flex items-center justify-between mb-2"><Label className="text-[10px] font-bold uppercase text-[#64748B]">Checklist ({form.checklist.length})</Label><Button size="sm" onClick={addChecklistItem} variant="outline" className="rounded-none text-[10px]" data-testid="add-checklist-btn"><Plus size={11} className="mr-1" />Add</Button></div>
              {form.checklist.map((c, i) => (
                <div key={i} className="flex gap-2 mb-1" data-testid={`checklist-${i}`}>
                  <span className="text-[10px] text-[#94A3B8] mt-2 w-4">{i+1}.</span>
                  <Input value={c.text} onChange={e => updateChecklistItem(i, e.target.value)} className="rounded-none text-xs h-7 flex-1" placeholder="Checklist item..." />
                  {form.checklist.length > 1 && <button onClick={() => removeChecklistItem(i)} className="text-[#DC2626]"><Trash2 size={12} /></button>}
                </div>
              ))}
            </div>
            <Button onClick={handleCreate} className="w-full bg-[#2563EB] text-white rounded-none text-xs font-bold" data-testid="audit-submit-btn">Create Audit</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Audit Detail */}
      <Dialog open={!!detailAudit} onOpenChange={(o) => { if (!o) { setDetailAudit(null); setFindings([]); } }}>
        <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="audit-detail-dialog">
          <DialogHeader>
            <DialogTitle>{detailAudit?.audit_number} — {detailAudit?.title}</DialogTitle>
            <DialogDescription className="text-xs text-[#64748B]">Audit details, checklist, and findings.</DialogDescription>
          </DialogHeader>
          {detailAudit && (
            <div className="space-y-4 mt-2">
              <div className="flex gap-2 flex-wrap"><DropdownBadge dropdownKey="audit_statuses" value={detailAudit.status} /><DropdownBadge dropdownKey="audit_types" value={detailAudit.audit_type} />{detailAudit.lead_auditor && <span className="text-[10px] px-2 py-0.5 bg-[#F8FAFC] border border-[#E2E8F0] text-[#64748B]">Lead: {detailAudit.lead_auditor}</span>}</div>
              {detailAudit.scope && <div><Label className="text-[10px] font-bold uppercase text-[#64748B]">Scope</Label><p className="text-sm text-[#0F172A] mt-1">{detailAudit.scope}</p></div>}
              {NEXT[detailAudit.status] && <Button size="sm" onClick={() => advance(detailAudit.id, NEXT[detailAudit.status])} className="rounded-none bg-[#2563EB] text-white text-xs font-bold"><ArrowRight size={12} className="mr-1" />Advance to {NEXT[detailAudit.status].replace(/_/g, " ")}</Button>}

              {/* Checklist */}
              {(detailAudit.checklist || []).length > 0 && (
                <div>
                  <Label className="text-[10px] font-bold uppercase text-[#64748B]">Checklist</Label>
                  <div className="space-y-1 mt-1">{detailAudit.checklist.map((c, i) => (
                    <div key={c.id || i} className="flex items-center gap-2 text-xs p-1.5 border border-[#F1F5F9]">
                      <span className={`w-5 h-5 flex items-center justify-center text-[9px] font-bold ${c.status === "completed" ? "bg-[#10B981] text-white" : "bg-[#E2E8F0] text-[#64748B]"}`}>{c.status === "completed" ? "✓" : i+1}</span>
                      <span className={c.status === "completed" ? "line-through text-[#94A3B8]" : "text-[#0F172A]"}>{c.text}</span>
                    </div>
                  ))}</div>
                </div>
              )}

              {/* Findings */}
              <div className="border-t border-[#E2E8F0] pt-3">
                <Label className="text-[10px] font-bold uppercase text-[#64748B]">Findings ({findings.length})</Label>
                {findings.map(f => (
                  <div key={f.id} className="p-2.5 border border-[#E2E8F0] mt-1 text-xs" data-testid={`finding-${f.id}`}>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-[#7C3AED]">{f.finding_number}</span>
                      <DropdownBadge dropdownKey="finding_severities" value={f.severity} />
                      <span className={`ml-auto text-[10px] font-bold ${f.status === "closed" ? "text-[#10B981]" : "text-[#D97706]"}`}>{f.status}</span>
                      {f.status !== "closed" && <Button size="sm" onClick={() => closeFinding(f.id)} className="rounded-none bg-[#10B981] text-white text-[9px] h-5 px-2"><CheckCircle2 size={9} className="mr-0.5" />Close</Button>}
                    </div>
                    <p className="text-[#0F172A] mt-1 font-medium">{f.title}</p>
                    {f.description && <p className="text-[#64748B] mt-0.5">{f.description}</p>}
                  </div>
                ))}
                {/* Add Finding */}
                <div className="mt-2 p-2.5 border border-dashed border-[#E2E8F0]">
                  <div className="grid grid-cols-2 gap-2 mb-1.5">
                    <Input value={findingForm.title} onChange={e => setFindingForm(f => ({ ...f, title: e.target.value }))} placeholder="Finding title..." className="rounded-none text-[10px] h-7" data-testid="finding-title" />
                    <DropdownSelect dropdownKey="finding_severities" value={findingForm.severity} onChange={e => setFindingForm(f => ({ ...f, severity: e.target.value }))} className="border border-[#E2E8F0] px-2 py-1 text-[10px]" testId="finding-severity" />
                  </div>
                  <div className="flex gap-2">
                    <Input value={findingForm.description} onChange={e => setFindingForm(f => ({ ...f, description: e.target.value }))} placeholder="Description..." className="rounded-none text-[10px] h-7 flex-1" data-testid="finding-desc" />
                    <Button size="sm" onClick={addFinding} className="rounded-none bg-[#7C3AED] text-white text-[9px] h-7" data-testid="add-finding-btn"><Plus size={10} /></Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
